import { Card } from '../../../components/ui/card'
import { cn } from '../../../lib/utils'
import { SECTION_LABEL } from '../../IngestPage/shared/compact'
import { changedSections, scriptChange } from '../util/draftChanges'
import { ChangeNote } from './ChangeNote'
import { collapseContext, lineDiff } from '../util/lineDiff'
import type { ConfigSubject } from '../util/subject'
import { formatWhen } from '../util/formatMeta'

/** Code keeps its columns: the block scrolls sideways inside its card, never the page. */
const CODE_BLOCK = 'max-h-[32rem] overflow-auto p-3 font-mono text-11 leading-[1.15rem]'

export function ScriptTab({ subject }: { subject: ConfigSubject }) {
  const script = subject.script
  if (!script) {
    return <p className="text-xs text-ink-3">An agent parser has no script.</p>
  }
  const changed = changedSections(subject.diff).has('script')
  const change = changed ? scriptChange(subject.diff) : undefined
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2 text-11 text-ink-3">
        {subject.compareLabel ? (
          <ChangeNote
            section="Script"
            changed={changed}
            compareLabel={subject.compareLabel}
            changedText="the diff is shown"
          />
        ) : (
          <span />
        )}
        <p className="font-mono">
          {script.entrypoint} · {script.imageVersion} · verified deterministic{' '}
          {formatWhen(script.verifiedAt)}
        </p>
      </div>
      {subject.notes.length ? (
        <Card className="gap-1.5 p-3">
          <p className={SECTION_LABEL}>Builder notes</p>
          <ul className="list-disc pl-4 text-xs leading-5">
            {subject.notes.map((note, index) => (
              <li key={index}>{note}</li>
            ))}
          </ul>
        </Card>
      ) : null}
      <Card className="min-w-0 overflow-hidden p-0">
        {change ? (
          <ScriptDiff before={change.before} after={change.after} />
        ) : (
          <pre className={CODE_BLOCK} data-testid="script-source">
            {script.script}
          </pre>
        )}
      </Card>
    </div>
  )
}

function ScriptDiff({ before, after }: { before: string; after: string }) {
  const hunks = collapseContext(lineDiff(before, after))
  return (
    <pre className={CODE_BLOCK} data-testid="script-diff">
      {hunks.map((hunk, hunkIndex) =>
        hunk.kind === 'collapsed' ? (
          <div key={hunkIndex} className="my-1 text-ink-3 select-none">
            … {hunk.count} unchanged line{hunk.count === 1 ? '' : 's'}
          </div>
        ) : (
          hunk.lines.map((line, lineIndex) => (
            <div
              key={`${hunkIndex}-${lineIndex}`}
              data-diff={line.kind}
              className={cn(
                '-mx-3 px-3 whitespace-pre',
                line.kind === 'add' && 'bg-st-run-bg text-st-run',
                line.kind === 'del' && 'bg-st-down-bg text-st-down'
              )}
            >
              <span className="inline-block w-4 select-none text-ink-3">
                {line.kind === 'add' ? '+' : line.kind === 'del' ? '-' : ' '}
              </span>
              {line.text}
            </div>
          ))
        )
      )}
    </pre>
  )
}
